import { useCallback } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Platform } from 'react-native';

import { useIsNavigationReady } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import {
  type MenuContentDeliveryDetailsData,
  type MenuContentOrderChannel,
} from '../../MenuContent.types';

// ─────────────────────────────────────────────────────────────────────────────

export const useMenuContentNavigation = (params: UseMenuContentNavigationParams) => {
  const { restaurantSlug, orderChannel, deliveryDetailsData } = params;

  const router = useRouter();
  const isNavigationReady = useIsNavigationReady();
  const routeParams = useLocalSearchParams<MaybeParamsWithCategorySlug>();
  const { trackEvent } = useTelemetry();

  const defaultActiveTargetId = routeParams.category;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const changeLocation = useCallback(() => {
    trackEvent({ name: 'menu.change_location' });

    router.navigate('/locations');
  }, [router, trackEvent]);

  const generateCustomizationHref = useCallback(
    (productSlug: string): string => {
      if (orderChannel === 'delivery') {
        const addressId = deliveryDetailsData?.addressId ?? '';

        return `/delivery/${addressId}/${productSlug}`;
      }

      return `/${restaurantSlug}/${productSlug}`;
    },
    [deliveryDetailsData?.addressId, orderChannel, restaurantSlug],
  );

  const setTargetParam = useCallback(
    (targetSlug: string) => {
      if (Platform.OS !== 'web' || !isNavigationReady) return;

      router.setParams({ category: targetSlug });
    },
    [isNavigationReady, router],
  );

  const openContactUs = useCallback(() => {
    router.navigate('/chat');
  }, [router]);

  return {
    defaultActiveTargetId,
    changeLocation,
    generateCustomizationHref,
    setTargetParam,
    openContactUs,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseMenuContentNavigationParams = {
  restaurantSlug: string;
  deliveryDetailsData?: MenuContentDeliveryDetailsData;
  orderChannel: MenuContentOrderChannel;
};

type MaybeParamsWithCategorySlug = { category?: string };
